import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarDays,
  ChevronRight,
  Circle,
  Clock,
  List,
  SquareCheckBig,
} from 'lucide-react';

import { usePageContext } from '@/hooks/usePageContext';
import { useAuth } from '@/hooks/useAuth';

import { PageHeader } from '@/components/layout/PageHeader';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { PatientLink } from '@/components/ui/PatientLink';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Spinner } from '@/components/ui/Spinner';
import { TaskDoneButton } from '@/components/tasks/TaskDoneButton';
import * as tasksSvc from '@/services/tasks.service';
import * as adminSvc from '@/services/admin.service';
import type { FilterOption } from '@/types/filters.types';
import { buildApiParams } from '@/lib/filterRegistry';
import {
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';

import { buildAllTasksHref, buildAssignedToOptions, canCompleteTask, priorityLabel, statusVariant, TASK_BUCKET_PENDING, TASK_FILTER_CONFIGS, TASK_FILTER_TABS, TASK_MEMBER_STATUS_OPTIONS, taskDueDate } from './tasksShared';

function taskTone(countKey: keyof tasksSvc.TaskCounts) {
  switch (countKey) {
    case 'pastDue':
      return 'red';
    case 'dueToday':
      return 'amber';
    case 'upcoming':
      return 'blue';
    case 'completed':
      return 'emerald';
    case 'inactive':
      return 'slate';
    default:
      return 'sky';
  }
}

function taskIcon(countKey: keyof tasksSvc.TaskCounts): React.ReactNode {
  const cls = 'h-4 w-4';
  switch (countKey) {
    case 'pastDue':
      return <AlertTriangle className={cls} />;
    case 'dueToday':
      return <Clock className={cls} />;
    case 'upcoming':
      return <CalendarDays className={cls} />;
    case 'completed':
      return <SquareCheckBig className={cls} />;
    case 'inactive':
      return <Circle className={cls} />;
    default:
      return <List className={cls} />;
  }
}

/**
 * Due dates are calendar dates. taskDueDate parses a date-only "YYYY-MM-DD" as
 * local midnight; a raw `new Date()` read it as UTC and rendered every date a
 * day early.
 */
function formatTaskDate(due: Date | null): string {
  if (!due) return 'No due date';
  return due.toLocaleDateString();
}

function formatActivityTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Same grouping StatCard applies to the metric row, so 99888 reads 99,888 in
// every count on the page.
const countFormatter = new Intl.NumberFormat('en-US');

function priorityColor(priority: number): string {
  switch (priority) {
    case 3:
      return 'text-red-500';
    case 2:
      return 'text-amber-500';
    default:
      return 'text-slate-400';
  }
}

export default function TasksDashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext('Tasks Dashboard');
  const { hasMinUserLevel } = useAuth();
  const isFilterAdmin = hasMinUserLevel(3);
  const [completingTaskId, setCompletingTaskId] = useState<number | null>(null);
  const visibleFilterConfigs = TASK_FILTER_CONFIGS;
  const filters = useLocalDashboardFilters(visibleFilterConfigs, 'tasks');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filters.values);
  const taskFilterParams = useMemo(
    () => buildApiParams(effectiveValues, filters.negations),
    [effectiveValues, filters.negations],
  );

  // placeholderData keeps the previous filter's numbers and rows on screen
  // while the next ones load, instead of blanking every card on each sidebar
  // change.
  const { data: counts, isLoading } = useQuery({
    queryKey: ['tasks-dashboard-counts', taskFilterParams],
    queryFn: () => tasksSvc.getTaskCounts(taskFilterParams),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const { data: myTasksData, isLoading: myTasksLoading } = useQuery({
    queryKey: ['tasks-dashboard-my-tasks', taskFilterParams],
    // FL_PENDING = Past Due + Due Today + Upcoming. Without it the endpoint
    // defaults to FL_UPCOMING and this card hides every overdue task.
    queryFn: () => tasksSvc.getMyTasks({
      page: 1,
      perPage: 7,
      ...taskFilterParams,
      flTasks: TASK_BUCKET_PENDING,
      sortBy: 'lastDue',
      sortDir: 'asc',
      // The card shows no total, and counting one doubles the request.
      withTotal: false,
    }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const { data: taskLogData, isLoading: taskLogLoading } = useQuery({
    queryKey: ['tasks-dashboard-log'],
    queryFn: () => tasksSvc.getLog({ page: 1, perPage: 6 }),
    staleTime: 60_000,
  });

  const { data: facilities } = useQuery({
    queryKey: ['tasks-dashboard-facilities'],
    queryFn: adminSvc.listFacilities,
    staleTime: 5 * 60_000,
  });

  const { data: assignedToRoles } = useQuery({
    queryKey: ['tasks-dashboard-my-roles'],
    queryFn: adminSvc.listMyRoles,
    staleTime: 5 * 60_000,
  });

  const { data: allAccountRoles } = useQuery({
    queryKey: ['tasks-dashboard-all-roles'],
    queryFn: adminSvc.listAccountRoles,
    staleTime: 5 * 60_000,
    enabled: isFilterAdmin,
  });

  const { data: allUsers } = useQuery({
    queryKey: ['tasks-dashboard-all-users'],
    queryFn: adminSvc.listUsers,
    staleTime: 5 * 60_000,
    enabled: isFilterAdmin,
  });

  const { data: taskGroups } = useQuery({
    queryKey: ['tasks-dashboard-task-groups'],
    queryFn: () => tasksSvc.listGroups({ page: 1, perPage: 200 }).then((r) => r.data ?? []),
    staleTime: 5 * 60_000,
  });

  function refreshTaskViews() {
    queryClient.invalidateQueries({ queryKey: ['tasks-dashboard-counts'] });
    queryClient.invalidateQueries({ queryKey: ['tasks-dashboard-my-tasks'] });
    queryClient.invalidateQueries({ queryKey: ['tasks-dashboard-log'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-my-tasks'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-task-counts'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
  }

  const completeMutation = useMutation({
    mutationFn: (taskId: number) => tasksSvc.completeTask(taskId),
    onSuccess: refreshTaskViews,
    onSettled: () => {
      setCompletingTaskId(null);
    },
  });

  const safeCounts: tasksSvc.TaskCounts = counts ?? {
    all: 0,
    upcoming: 0,
    pending: 0,
    actionable: 0,
    dueToday: 0,
    pastDue: 0,
    completed: 0,
    inactive: 0,
  };
  const myTasks = myTasksData?.data ?? [];
  const recentActivity = taskLogData?.data ?? [];
  const filterOptions = useMemo<Record<string, FilterOption[]>>(() => ({
    facility: (facilities ?? []).filter((facility) => facility.active).map((facility) => ({
      value: String(facility.facilityId),
      label: facility.facilityName || 'Unknown',
    })),
    memberStatus: TASK_MEMBER_STATUS_OPTIONS,
    assignedTo: isFilterAdmin
      ? buildAssignedToOptions(assignedToRoles ?? null, allAccountRoles ?? null, allUsers ?? null)
      : buildAssignedToOptions(assignedToRoles ?? null),
    priority: [
      { value: 'low', label: 'Low' },
      { value: 'normal', label: 'Medium' },
      { value: 'high', label: 'High' },
    ],
    taskGroup: (taskGroups ?? []).map((group) => ({ value: String(group.taskGroupID), label: group.name })),
  }), [facilities, taskGroups, assignedToRoles, allAccountRoles, allUsers, isFilterAdmin]);

  useEffect(() => {
    updateAIContext({
      dataSummary: `Past Due: ${safeCounts.pastDue}, Due Today: ${safeCounts.dueToday}, Upcoming: ${safeCounts.upcoming}, Complete: ${safeCounts.completed}, Inactive: ${safeCounts.inactive}, All Entries: ${safeCounts.all}, My Tasks: ${myTasks.length}, Recent Activity: ${recentActivity.length}`,
    });
  }, [myTasks.length, recentActivity.length, safeCounts.all, safeCounts.completed, safeCounts.dueToday, safeCounts.inactive, safeCounts.pastDue, safeCounts.upcoming, updateAIContext]);

  // Only the numbers wait on /tasks/counts. The page used to sit behind a
  // full-screen spinner — sidebar included — until the counts arrived, which
  // is the slowest request here, so nothing at all showed for its duration.
  const countsPending = isLoading && !counts;
  const countLabel = (value: number): string => (countsPending ? '—' : countFormatter.format(value));

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={visibleFilterConfigs}
          values={effectiveValues}
          options={filterOptions}
          negations={filters.negations}
          onChange={filters.setFilter}
          onNegateChange={filters.setNegation}
          onClear={filters.clearFilter}
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          storageKey="tasks"
          showAIAssistant={false}
        />
      )}
    >
      <div className="space-y-6 overflow-y-auto p-6">
      <PageHeader
        title="Tasks"
        actions={(
          <>
            <Button variant="secondary" onClick={() => navigate('/tasks/rules')}>
              Rules
            </Button>
            <Button variant="secondary" onClick={() => navigate('/tasks/all')}>
              All Tasks
            </Button>
          </>
        )}
      />

      <MetricCardGrid columns={6}>
        {TASK_FILTER_TABS.map((tab) => (
          <StatCard
            key={tab.value}
            label={tab.label}
            value={countLabel(safeCounts[tab.countKey])}
            tooltip={tab.tooltip}
            tone={taskTone(tab.countKey)}
            icon={taskIcon(tab.countKey)}
            onClick={() => navigate(buildAllTasksHref(tab.value))}
          />
        ))}
      </MetricCardGrid>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="xl:col-span-5">
          <Card className="h-full">
            <CardHeader
              action={(
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-primary h-7 px-2 gap-1"
                  onClick={() => navigate('/tasks/all')}
                >
                  View All <ChevronRight className="h-3 w-3" />
                </Button>
              )}
            >
              <CardTitle>My Tasks</CardTitle>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <div className="divide-y divide-border">
                {myTasksLoading ? (
                  <div className="flex items-center justify-center px-5 py-8">
                    <Spinner size="sm" />
                  </div>
                ) : myTasks.length === 0 ? (
                  <p className="px-5 py-8 text-center text-sm text-muted-foreground">No assigned tasks.</p>
                ) : (
                  myTasks.map((task) => {
                    // `||`, not `??`: the API maps name as (string)($row->name ?? ''),
                    // so canned tasks can legitimately arrive with an empty name.
                    const taskLabel = task.name?.trim() || `Task #${task.planTaskID}`;
                    const dueLabel = formatTaskDate(taskDueDate(task));
                    const isComplete = task.derivedStatus === 'Completed';
                    const isCompleting = completingTaskId === task.planTaskID;

                    return (
                      <div key={task.planTaskID} className="flex items-start gap-3 px-5 py-3">
                        <div className="mt-0.5 flex-shrink-0">
                          {isCompleting ? (
                            <Spinner size="sm" />
                          ) : isComplete ? (
                            <SquareCheckBig className="h-4 w-4 text-green-500" />
                          ) : (
                            <Circle className={`h-4 w-4 ${priorityColor(task.priority)}`} />
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium leading-snug text-foreground">{taskLabel}</p>
                          {task.patientName && ((task.taskPatientID ?? 0) > 0 ? (
                            <PatientLink
                              patientId={task.taskPatientID!}
                              patientName={task.patientName}
                              className="mt-0.5 block truncate text-xs text-blue-600 hover:underline"
                            >
                              {task.patientName}
                            </PatientLink>
                          ) : (
                            // A name with taskPatientID=0 comes from the plan's
                            // patient — displayable, but there is no id to link.
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">{task.patientName}</p>
                          ))}
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <Badge variant={statusVariant(task.derivedStatus)} size="sm">
                              {task.derivedStatus ?? 'N/A'}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                              <Clock className="h-2.5 w-2.5" />
                              {dueLabel}
                            </span>
                            <span className="text-[10px] text-muted-foreground">{priorityLabel(task.priority)}</span>
                          </div>
                        </div>

                        {canCompleteTask(task) && (
                          <TaskDoneButton
                            taskId={task.planTaskID}
                            size="xs"
                            className="shrink-0"
                            completeLoading={isCompleting}
                            onComplete={() => {
                              setCompletingTaskId(task.planTaskID);
                              completeMutation.mutate(task.planTaskID);
                            }}
                            onRescheduled={() => refreshTaskViews()}
                          />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="xl:col-span-3">
          <Card className="h-full">
            <CardHeader>
              <CardTitle>At Risk</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100"
                onClick={() => navigate(buildAllTasksHref(4))}
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <p className="text-sm font-medium text-slate-900">Past Due</p>
                </div>
                <span className="text-lg font-semibold text-red-700">{countLabel(safeCounts.pastDue)}</span>
              </button>

              <button
                type="button"
                className="flex w-full items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
                onClick={() => navigate(buildAllTasksHref(3))}
              >
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <p className="text-sm font-medium text-slate-900">Due Today</p>
                </div>
                <span className="text-lg font-semibold text-amber-700">{countLabel(safeCounts.dueToday)}</span>
              </button>

              <button
                type="button"
                className="flex w-full items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-left transition-colors hover:bg-blue-100"
                onClick={() => navigate(buildAllTasksHref(2))}
              >
                <div className="flex items-center gap-3">
                  <Circle className="h-4 w-4 text-blue-600" />
                  <p className="text-sm font-medium text-slate-900">Upcoming</p>
                </div>
                <span className="text-lg font-semibold text-blue-700">{countLabel(safeCounts.upcoming)}</span>
              </button>
            </CardBody>
          </Card>
        </div>

        <div className="xl:col-span-4">
          <Card className="h-full">
            <CardHeader
              action={(
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-primary h-7 px-2 gap-1"
                  onClick={() => navigate('/tasks/all')}
                >
                  All Tasks <ChevronRight className="h-3 w-3" />
                </Button>
              )}
            >
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <div className="divide-y divide-border">
                {taskLogLoading ? (
                  <div className="flex items-center justify-center px-5 py-8">
                    <Spinner size="sm" />
                  </div>
                ) : recentActivity.length === 0 ? (
                  <p className="px-5 py-8 text-center text-sm text-muted-foreground">No recent task activity.</p>
                ) : (
                  recentActivity.map((entry) => (
                    <div key={entry.taskLogID} className="px-5 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">
                            {entry.taskName ?? 'Task activity'}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {entry.action}
                            {entry.patientName ? ` · ${entry.patientName}` : ''}
                            {entry.performedByName ? ` · ${entry.performedByName}` : ''}
                          </p>
                          {entry.notes && (
                            <p className="mt-1 text-xs text-slate-500 line-clamp-2">{entry.notes}</p>
                          )}
                        </div>
                        <span className="shrink-0 text-[10px] text-muted-foreground">
                          {formatActivityTime(entry.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
      </div>
    </ListPageLayout>
  );
}
